import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';

import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
} from '@mui/material';

// Services
import proposalsServices from '../../../services/proposalsServices';

// Auto-save
import useAutoSave from '../../../hooks/useAutoSave';
import AutoSaveIndicator from '../../AutoSaveIndicator/AutoSaveIndicator';

const ProposalJobPriceSection = ({ proposal, id, refresh }) => {
  const [jobPrice, setJobPrice] = useState(0);
  const dirtyRef = useRef(false);

  // Auto-save function
  const saveJobPrice = useCallback(async (price) => {
    const response = await proposalsServices.update(id, { jobPrice: price });
    dirtyRef.current = false;
    if (response.status === 200 && refresh) {
      refresh();
    }
  }, [id, refresh]);

  // Auto-save on Enter and on blur — no timer (numeric field, a period is
  // the decimal separator so word-boundary saving does not apply here)
  const {
    isSaving,
    hasUnsavedChanges,
    lastSaved,
    error,
    markPending,
    saveNow,
    getLastSavedText,
  } = useAutoSave(saveJobPrice);

  const flushPending = useCallback(() => {
    saveNow().catch(() => {
      toast.error('Auto-save failed. Your changes will be retried on the next edit.');
    });
  }, [saveNow]);

  useEffect(() => {
    if (proposal?.jobPrice !== undefined && !dirtyRef.current) {
      setJobPrice(Number(proposal.jobPrice) || 0);
    }
  }, [proposal]);

  const handleChange = (e) => {
    const newValue = Number(e.target.value) || 0;
    dirtyRef.current = true;
    setJobPrice(newValue);
    markPending(newValue);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Job Price</Typography>
        <AutoSaveIndicator
          isSaving={isSaving}
          hasUnsavedChanges={hasUnsavedChanges}
          lastSaved={lastSaved}
          error={error}
          getLastSavedText={getLastSavedText}
          variant="chip"
        />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormControl size="small" sx={{ width: 200 }}>
          <InputLabel>Job Price</InputLabel>
          <OutlinedInput
            type="number"
            value={jobPrice || ''}
            onChange={handleChange}
            onBlur={flushPending}
            onKeyUp={(e) => { if (e.key === 'Enter') flushPending(); }}
            startAdornment={<InputAdornment position="start">$</InputAdornment>}
            label="Job Price"
          />
        </FormControl>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Total labor cost for this service job
      </Typography>
    </Box>
  );
};

export default ProposalJobPriceSection;
